import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { MigrationCaseDetailsResponse, MigrationWizardChecks, MigrationWizardStepData } from '@/lib/api';
import WizardShell from '@/components/ui/wizard-shell';
import { WizardCard, WizardKVGrid, WizardKVItem, WizardSection } from '@/components/ui/wizard-layout';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';

type Props = {
  open: boolean;
  caseId: number | null;
  onOpenChange: (open: boolean) => void;
  onCompleted?: () => void;
  getCaseDetails: (caseId: number) => Promise<MigrationCaseDetailsResponse>;
  checkCaseWizard: (caseId: number, stepData?: MigrationWizardStepData) => Promise<MigrationWizardChecks>;
  saveCaseWizardDraft: (caseId: number, stepData: MigrationWizardStepData, notes?: string | null) => Promise<{ checks?: MigrationWizardChecks }>;
  previewCaseWizardConfirm: (caseId: number, stepData?: MigrationWizardStepData) => Promise<{ can_confirm: boolean; checks: MigrationWizardChecks }>;
  confirmCaseMigration: (caseId: number, payload?: Record<string, unknown>) => Promise<{ ok: boolean; status?: string }>;
};

type MappingField = 'teacher' | 'unterrichtsform' | 'weekday' | 'start_time';
type MappingOrigin = 'suggested' | 'manual' | null;

const EMPTY_STEP_DATA: MigrationWizardStepData = {
  data_check: {
    confirmed: false,
    notes: '',
    user_profile: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address_street: '',
      address_zip: '',
      address_city: '',
    },
  },
  linked_people_check: { confirmed: false, notes: '' },
  courses_history_check: {
    confirmed: false,
    migrate_courses: true,
    create_missing_courses: false,
    import_history: true,
    notes: '',
    target_course_id: undefined,
    target_teacher_wp_user_id: undefined,
    target_unterrichtsform_id: undefined,
    target_weekday: undefined,
    target_start_time: '',
  },
};

const weekdayLabel = (weekday?: number | null): string => {
  const map: Record<number, string> = {
    1: 'Montag',
    2: 'Dienstag',
    3: 'Mittwoch',
    4: 'Donnerstag',
    5: 'Freitag',
    6: 'Samstag',
    7: 'Sonntag',
  };
  if (!weekday || !map[weekday]) return '-';
  return map[weekday];
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message;
  return 'Unbekannter Fehler';
};

const preferNonEmptyString = (...values: Array<string | undefined | null>): string => {
  for (const value of values) {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (normalized !== '') {
      return value as string;
    }
  }
  return '';
};

const PersonMigrationWizardModal: React.FC<Props> = ({
  open,
  caseId,
  onOpenChange,
  onCompleted,
  getCaseDetails,
  checkCaseWizard,
  saveCaseWizardDraft,
  previewCaseWizardConfirm,
  confirmCaseMigration,
}) => {
  const [step, setStep] = useState(1);
  const [isBusy, setIsBusy] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [details, setDetails] = useState<MigrationCaseDetailsResponse | null>(null);
  const [checks, setChecks] = useState<MigrationWizardChecks | null>(null);
  const [stepData, setStepData] = useState<MigrationWizardStepData>(EMPTY_STEP_DATA);
  const [globalNotes, setGlobalNotes] = useState('');
  const [mappingOrigins, setMappingOrigins] = useState<Record<MappingField, MappingOrigin>>({
    teacher: null,
    unterrichtsform: null,
    weekday: null,
    start_time: null,
  });

  useEffect(() => {
    if (!open || !caseId) return;
    let cancelled = false;
    setStep(1);
    setIsLoadingDetails(true);
    setDetails(null);
    setChecks(null);
    setMappingOrigins({
      teacher: null,
      unterrichtsform: null,
      weekday: null,
      start_time: null,
    });

    getCaseDetails(caseId)
      .then((res) => {
        if (cancelled) return;
        setDetails(res);
        setChecks(res.checks);
        const legacyProfileFallback = {
          first_name: res.case.legacy_contract?.student?.first_name ?? '',
          last_name: res.case.legacy_contract?.student?.last_name ?? '',
        };
        const existingProfile = (res.wizard?.step_data?.data_check?.user_profile ?? {}) as Record<string, string>;
        const sourceProfile = (res.user_profile ?? {}) as Record<string, string>;
        const mergedStepData: MigrationWizardStepData = {
          ...EMPTY_STEP_DATA,
          ...(res.wizard?.step_data ?? {}),
        };
        mergedStepData.data_check = {
          ...(EMPTY_STEP_DATA.data_check ?? {}),
          ...(mergedStepData.data_check ?? {}),
          user_profile: {
            ...(EMPTY_STEP_DATA.data_check?.user_profile ?? {}),
            first_name: preferNonEmptyString(existingProfile.first_name, sourceProfile.first_name, legacyProfileFallback.first_name),
            last_name: preferNonEmptyString(existingProfile.last_name, sourceProfile.last_name, legacyProfileFallback.last_name),
            email: preferNonEmptyString(existingProfile.email, sourceProfile.email),
            phone: preferNonEmptyString(existingProfile.phone, sourceProfile.phone),
            address_street: preferNonEmptyString(existingProfile.address_street, sourceProfile.address_street),
            address_zip: preferNonEmptyString(existingProfile.address_zip, sourceProfile.address_zip),
            address_city: preferNonEmptyString(existingProfile.address_city, sourceProfile.address_city),
          },
        };
        setStepData(mergedStepData);
        setGlobalNotes((res.wizard?.notes as string) ?? '');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        toast.error(getErrorMessage(err) || 'Case-Details konnten nicht geladen werden');
        onOpenChange(false);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDetails(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, caseId, getCaseDetails, onOpenChange]);

  const caseSnapshot = details?.case.snapshot ?? {};
  const legacyContract = details?.case.legacy_contract;
  const currentStatus = details?.case.status ?? '';
  const targetOptions = details?.target_options;
  const profile = stepData.data_check?.user_profile ?? {};
  const legacyTeacherMissing = (checks?.computed.legacy_teacher_id ?? 0) <= 0;
  const legacyUformMissing = (checks?.computed.legacy_uform_id ?? 0) <= 0;
  const legacyWeekdayMissing = (checks?.computed.legacy_weekday ?? 0) <= 0;
  const legacyStartTimeMissing = !checks?.computed.legacy_start_time;
  const legacyCourseMissing = (checks?.computed.legacy_course_id ?? 0) <= 0;
  const legacyWeekday = checks?.computed.legacy_weekday ?? 0;
  const legacyStartTimeRaw = checks?.computed.legacy_start_time ?? '';
  const legacyStartTime = legacyStartTimeRaw ? String(legacyStartTimeRaw).slice(0, 5) : '';
  const legacyDuration = caseSnapshot.legacy_duration_min ? Number(caseSnapshot.legacy_duration_min) : null;

  const canProceedStep1 =
    !!stepData.data_check?.confirmed &&
    (profile.first_name ?? '').trim() !== '' &&
    (profile.last_name ?? '').trim() !== '';
  const canProceedStep2 = !!stepData.linked_people_check?.confirmed;
  const canProceedStep3 =
    !!stepData.courses_history_check?.confirmed &&
    !!(
      stepData.courses_history_check?.migrate_courses ||
      stepData.courses_history_check?.create_missing_courses ||
      stepData.courses_history_check?.import_history
    ) &&
    (!legacyTeacherMissing || !!stepData.courses_history_check?.target_teacher_wp_user_id) &&
    (!legacyUformMissing || !!stepData.courses_history_check?.target_unterrichtsform_id) &&
    (!legacyWeekdayMissing || !!stepData.courses_history_check?.target_weekday) &&
    (!legacyStartTimeMissing || (stepData.courses_history_check?.target_start_time ?? '').trim() !== '') &&
    (!legacyCourseMissing || !!stepData.courses_history_check?.target_course_id);
  const canProceedStep4 = !!checks?.can_confirm;
  const legacyStudent = legacyContract?.student;
  const legacyStudentName = [legacyStudent?.last_name, legacyStudent?.first_name].filter(Boolean).join(', ');

  const steps = useMemo(
    () => [
      { label: 'Datencheck' },
      { label: 'Zuordnungen' },
      { label: 'Kurse & Historie' },
      { label: 'Vorschau' },
      { label: 'Abschluss' },
    ],
    []
  );

  const applyCourseAutofill = useCallback((courseId: number | undefined, onlyMissing = false) => {
    const selectedCourse = targetOptions?.courses?.find((course) => course.id === courseId);
    if (!selectedCourse) return;
    const touched: MappingField[] = [];
    setStepData((prev) => {
      const current = prev.courses_history_check ?? {};
      const next = { ...current };
      const applyNumber = (currentValue: number | undefined, nextValue?: number | null) =>
        onlyMissing ? (currentValue && currentValue > 0 ? currentValue : nextValue ?? undefined) : nextValue ?? undefined;
      const applyString = (currentValue: string | undefined, nextValue?: string | null) =>
        onlyMissing ? ((currentValue ?? '').trim() !== '' ? currentValue : nextValue ?? undefined) : nextValue ?? undefined;

      const nextTeacher = applyNumber(next.target_teacher_wp_user_id, selectedCourse.teacher_wp_user_id);
      const nextUform = applyNumber(next.target_unterrichtsform_id, selectedCourse.unterrichtsform_id);
      const nextWeekday = applyNumber(next.target_weekday, selectedCourse.weekday);
      const nextStart = applyString(next.target_start_time, selectedCourse.time_start ? String(selectedCourse.time_start).slice(0, 5) : null);
      if (nextTeacher !== next.target_teacher_wp_user_id) touched.push('teacher');
      if (nextUform !== next.target_unterrichtsform_id) touched.push('unterrichtsform');
      if (nextWeekday !== next.target_weekday) touched.push('weekday');
      if (nextStart !== next.target_start_time) touched.push('start_time');
      next.target_teacher_wp_user_id = nextTeacher;
      next.target_unterrichtsform_id = nextUform;
      next.target_weekday = nextWeekday;
      next.target_start_time = nextStart;

      return {
        ...prev,
        courses_history_check: next,
      };
    });
    if (touched.length > 0) {
      setMappingOrigins((prev) => {
        const next = { ...prev };
        touched.forEach((field) => {
          next[field] = 'suggested';
        });
        return next;
      });
    }
  }, [targetOptions]);

  useEffect(() => {
    if (!details) return;
    if (stepData.courses_history_check?.target_course_id) return;
    const options = targetOptions?.courses ?? [];
    if (options.length === 0) return;

    const scoreCourse = (course: NonNullable<typeof targetOptions>['courses'][number]): number => {
      let score = 0;
      if (legacyWeekday > 0 && course.weekday === legacyWeekday) score += 4;
      if (legacyStartTime !== '' && course.time_start && String(course.time_start).slice(0, 5) === legacyStartTime) score += 4;
      if (legacyDuration !== null && course.dauer_min === legacyDuration) score += 2;
      return score;
    };

    const sorted = [...options].sort((a, b) => scoreCourse(b) - scoreCourse(a));
    const best = sorted[0];
    if (!best || scoreCourse(best) <= 0) return;

    setStepData((prev) => ({
      ...prev,
      courses_history_check: {
        ...(prev.courses_history_check ?? {}),
        target_course_id: best.id,
      },
    }));
    applyCourseAutofill(best.id, true);
  }, [details, targetOptions, stepData.courses_history_check?.target_course_id, legacyWeekday, legacyStartTime, legacyDuration, applyCourseAutofill]);

  const persistDraft = async (showToast = false) => {
    if (!caseId) return;
    const saved = await saveCaseWizardDraft(caseId, stepData, globalNotes || null);
    setChecks(saved.checks ?? checks);
    if (showToast) {
      toast.success('Zwischenstand gespeichert');
    }
  };

  const runChecks = async () => {
    if (!caseId) return;
    const nextChecks = await checkCaseWizard(caseId, stepData);
    setChecks(nextChecks);
    return nextChecks;
  };

  const handleNext = async () => {
    if (!caseId) return;
    try {
      setIsBusy(true);
      if (step === 1 && !canProceedStep1) {
        toast.error('Bitte Datencheck bestätigen');
        return;
      }
      if (step === 2 && !canProceedStep2) {
        toast.error('Bitte Zuordnungen bestätigen');
        return;
      }
      if (step === 3 && !canProceedStep3) {
        toast.error('Bitte Kurse/Historie bestätigen und mindestens eine Aktion wählen');
        return;
      }

      await persistDraft(false);
      await runChecks();

      if (step === 4) {
        const preview = await previewCaseWizardConfirm(caseId, stepData);
        setChecks(preview.checks);
        if (!preview.can_confirm) {
          toast.error('Finale Vorschau ist noch nicht freigabefähig');
          return;
        }
      }

      if (step < 5) {
        setStep((prev) => prev + 1);
      }
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || 'Weitergehen fehlgeschlagen');
    } finally {
      setIsBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!caseId) return;
    try {
      setIsBusy(true);
      await persistDraft(false);
      const preview = await previewCaseWizardConfirm(caseId, stepData);
      setChecks(preview.checks);
      if (!preview.can_confirm) {
        toast.error('Case kann noch nicht bestätigt werden');
        return;
      }

      await confirmCaseMigration(caseId, {
        draft: stepData,
        notes: globalNotes || null,
      });
      toast.success('Case erfolgreich als migriert bestätigt');
      onOpenChange(false);
      onCompleted?.();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || 'Bestätigung fehlgeschlagen');
    } finally {
      setIsBusy(false);
    }
  };

  if (!open || !caseId) return null;

  const originBadge = (field: MappingField) => {
    const origin = mappingOrigins[field];
    if (origin === 'suggested') {
      return <span className="ml-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-300">Vorschlag</span>;
    }
    if (origin === 'manual') {
      return <span className="ml-2 rounded-full border border-blue-500/40 bg-blue-500/10 px-2 py-0.5 text-[10px] text-blue-300">manuell</span>;
    }
    return null;
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title={`Personenmigration bearbeiten (Case #${caseId})`}
      description={`Schritt ${step} von 5`}
      currentStep={step}
      steps={steps}
      onBack={step > 1 ? () => setStep((prev) => prev - 1) : undefined}
      canBack={step > 1 && !isBusy}
      onNext={step < 5 ? handleNext : undefined}
      canNext={
        !isBusy &&
        !isLoadingDetails &&
        (step === 1 ? canProceedStep1 : step === 2 ? canProceedStep2 : step === 3 ? canProceedStep3 : step === 4 ? canProceedStep4 : false)
      }
      onPrimary={step === 5 ? handleSubmit : undefined}
      canPrimary={step === 5 && !isBusy && !!checks?.can_confirm}
      primaryLabel={isBusy ? 'Wird bestätigt...' : 'Migration final bestätigen'}
      isLoading={isBusy}
    >
      <div className="py-4">
        {isLoadingDetails && <div className="text-sm text-muted-foreground">Case wird geladen...</div>}

        {!isLoadingDetails && details && (
          <div className="space-y-4">
            {step === 1 && (
              <WizardCard>
                <WizardSection title="Case-Kontext">
                  <WizardKVGrid>
                    <WizardKVItem label="Status">{currentStatus}</WizardKVItem>
                    <WizardKVItem label="WP-User">{details.case.wp_user_id ?? '-'}</WizardKVItem>
                    <WizardKVItem label="Schülerprofil">{details.case.subprofile_id ?? '-'}</WizardKVItem>
                    <WizardKVItem label="Zuordnung">{details.user_profile?.subprofile_assignment_text ?? '-'}</WizardKVItem>
                    <WizardKVItem label="Legacy Person">{details.case.legacy_person_id}</WizardKVItem>
                    <WizardKVItem label="Legacy Vertrag">{details.case.legacy_contract_id}</WizardKVItem>
                    <WizardKVItem label="Legacy Beginn">{String(caseSnapshot.legacy_begin ?? '-')}</WizardKVItem>
                    <WizardKVItem label="Legacy Ende">{String(caseSnapshot.legacy_end ?? '-')}</WizardKVItem>
                    <WizardKVItem label="Schüler (Alt-System)">{legacyStudentName || '-'}</WizardKVItem>
                    <WizardKVItem label="Geburtsdatum (Alt-System)">{legacyStudent?.birth_date ?? '-'}</WizardKVItem>
                    <WizardKVItem label="Vertragspartner (Alt-System)">{legacyContract?.contract_partner?.name ?? '-'}</WizardKVItem>
                  </WizardKVGrid>
                </WizardSection>
                <WizardSection title="User-Daten (korrigierbar)">
                  <p className="mb-3 text-xs text-muted-foreground">
                    Die Felder sind vorbefüllt. Werte aus dem Alt-System werden unter den Eingaben angezeigt und können bei Bedarf korrigiert werden.
                  </p>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="text-sm">
                      <div className="mb-1 text-muted-foreground">Vorname</div>
                      <input
                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                        value={profile.first_name ?? ''}
                        onChange={(e) =>
                          setStepData((prev) => ({
                            ...prev,
                            data_check: {
                              ...(prev.data_check ?? {}),
                              user_profile: {
                                ...(prev.data_check?.user_profile ?? {}),
                                first_name: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                      <div className="mt-1 text-[11px] text-muted-foreground">Alt-System: {legacyStudent?.first_name ?? '-'}</div>
                    </label>
                    <label className="text-sm">
                      <div className="mb-1 text-muted-foreground">Nachname</div>
                      <input
                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                        value={profile.last_name ?? ''}
                        onChange={(e) =>
                          setStepData((prev) => ({
                            ...prev,
                            data_check: {
                              ...(prev.data_check ?? {}),
                              user_profile: {
                                ...(prev.data_check?.user_profile ?? {}),
                                last_name: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                      <div className="mt-1 text-[11px] text-muted-foreground">Alt-System: {legacyStudent?.last_name ?? '-'}</div>
                    </label>
                    <label className="text-sm">
                      <div className="mb-1 text-muted-foreground">E-Mail</div>
                      <input
                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                        value={profile.email ?? ''}
                        onChange={(e) =>
                          setStepData((prev) => ({
                            ...prev,
                            data_check: {
                              ...(prev.data_check ?? {}),
                              user_profile: {
                                ...(prev.data_check?.user_profile ?? {}),
                                email: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                      <div className="mt-1 text-[11px] text-muted-foreground">Quelle: WP-Profil / Alt-System-Fallback</div>
                    </label>
                    <label className="text-sm">
                      <div className="mb-1 text-muted-foreground">Telefon</div>
                      <input
                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                        value={profile.phone ?? ''}
                        onChange={(e) =>
                          setStepData((prev) => ({
                            ...prev,
                            data_check: {
                              ...(prev.data_check ?? {}),
                              user_profile: {
                                ...(prev.data_check?.user_profile ?? {}),
                                phone: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                      <div className="mt-1 text-[11px] text-muted-foreground">Quelle: WP-Profil / Alt-System-Fallback</div>
                    </label>
                    <label className="text-sm md:col-span-2">
                      <div className="mb-1 text-muted-foreground">Straße / Hausnummer</div>
                      <input
                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                        value={profile.address_street ?? ''}
                        onChange={(e) =>
                          setStepData((prev) => ({
                            ...prev,
                            data_check: {
                              ...(prev.data_check ?? {}),
                              user_profile: {
                                ...(prev.data_check?.user_profile ?? {}),
                                address_street: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                    </label>
                    <label className="text-sm">
                      <div className="mb-1 text-muted-foreground">PLZ</div>
                      <input
                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                        value={profile.address_zip ?? ''}
                        onChange={(e) =>
                          setStepData((prev) => ({
                            ...prev,
                            data_check: {
                              ...(prev.data_check ?? {}),
                              user_profile: {
                                ...(prev.data_check?.user_profile ?? {}),
                                address_zip: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                    </label>
                    <label className="text-sm">
                      <div className="mb-1 text-muted-foreground">Ort</div>
                      <input
                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                        value={profile.address_city ?? ''}
                        onChange={(e) =>
                          setStepData((prev) => ({
                            ...prev,
                            data_check: {
                              ...(prev.data_check ?? {}),
                              user_profile: {
                                ...(prev.data_check?.user_profile ?? {}),
                                address_city: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                    </label>
                  </div>
                </WizardSection>
                <WizardSection title="Datencheck bestätigen">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={!!stepData.data_check?.confirmed}
                      onCheckedChange={(checked) =>
                        setStepData((prev) => ({
                          ...prev,
                          data_check: {
                            ...(prev.data_check ?? {}),
                            confirmed: Boolean(checked),
                          },
                        }))
                      }
                    />
                    Daten wurden geprüft und sind für die Migration geeignet
                  </label>
                  <Textarea
                    className="mt-3"
                    placeholder="Hinweise zum Datencheck (optional)"
                    value={stepData.data_check?.notes ?? ''}
                    onChange={(e) =>
                      setStepData((prev) => ({
                        ...prev,
                        data_check: {
                          ...(prev.data_check ?? {}),
                          notes: e.target.value,
                        },
                      }))
                    }
                  />
                </WizardSection>
              </WizardCard>
            )}

            {step === 2 && (
              <WizardCard>
                <WizardSection title="Zuordnungen">
                  <WizardKVGrid>
                    <WizardKVItem label="WP-User vorhanden">{checks?.computed.has_wp_user ? 'ja' : 'nein'}</WizardKVItem>
                    <WizardKVItem label="Schülerprofil vorhanden">{checks?.computed.has_subprofile ? 'ja' : 'nein'}</WizardKVItem>
                    <WizardKVItem label="Schülerprofil-ID">{details.case.subprofile_id ?? '-'}</WizardKVItem>
                  </WizardKVGrid>
                </WizardSection>
                <WizardSection title="Zuordnung bestätigen">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={!!stepData.linked_people_check?.confirmed}
                      onCheckedChange={(checked) =>
                        setStepData((prev) => ({
                          ...prev,
                          linked_people_check: {
                            ...(prev.linked_people_check ?? {}),
                            confirmed: Boolean(checked),
                          },
                        }))
                      }
                    />
                    Zugeordnete Personen wurden geprüft
                  </label>
                  <Textarea
                    className="mt-3"
                    placeholder="Hinweise zu Zuordnungen (optional)"
                    value={stepData.linked_people_check?.notes ?? ''}
                    onChange={(e) =>
                      setStepData((prev) => ({
                        ...prev,
                        linked_people_check: {
                          ...(prev.linked_people_check ?? {}),
                          notes: e.target.value,
                        },
                      }))
                    }
                  />
                </WizardSection>
              </WizardCard>
            )}

            {step === 3 && (
              <WizardCard>
                <WizardSection title="Legacy-Kursdaten">
                  <WizardKVGrid>
                    <WizardKVItem label="Legacy Kurs">{checks?.computed.legacy_course_id ?? 0}</WizardKVItem>
                    <WizardKVItem label="Legacy Kursname">{String(caseSnapshot.legacy_course_name ?? '-')}</WizardKVItem>
                    <WizardKVItem label="Legacy Lehrer">{checks?.computed.legacy_teacher_id ?? 0}</WizardKVItem>
                    <WizardKVItem label="Legacy Lehrername">{String(caseSnapshot.legacy_teacher_name ?? '-')}</WizardKVItem>
                    <WizardKVItem label="Legacy Unterrichtsform">{checks?.computed.legacy_uform_id ?? 0}</WizardKVItem>
                    <WizardKVItem label="Legacy U-Form Name">{String(caseSnapshot.legacy_uform_name ?? '-')}</WizardKVItem>
                    <WizardKVItem label="Legacy Wochentag">{weekdayLabel(checks?.computed.legacy_weekday ?? null)}</WizardKVItem>
                    <WizardKVItem label="Legacy Uhrzeit">{checks?.computed.legacy_start_time ?? '-'}</WizardKVItem>
                    <WizardKVItem label="Legacy Dauer">{caseSnapshot.legacy_duration_min ? `${String(caseSnapshot.legacy_duration_min)} Min.` : '-'}</WizardKVItem>
                  </WizardKVGrid>
                </WizardSection>
                <WizardSection title="Manuelle Zuordnung (neues System)">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="text-sm">
                      <div className="mb-1 text-muted-foreground">Ziel-Lehrer{originBadge('teacher')}</div>
                      <select
                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                        value={stepData.courses_history_check?.target_teacher_wp_user_id ?? ''}
                        onChange={(e) => {
                          setStepData((prev) => ({
                            ...prev,
                            courses_history_check: {
                              ...(prev.courses_history_check ?? {}),
                              target_teacher_wp_user_id: e.target.value ? Number(e.target.value) : undefined,
                            },
                          }));
                          setMappingOrigins((prev) => ({ ...prev, teacher: 'manual' }));
                        }}
                      >
                        <option value="">Bitte wählen</option>
                        {targetOptions?.teachers?.map((teacher) => (
                          <option key={teacher.wp_user_id} value={teacher.wp_user_id}>
                            {teacher.name} (#{teacher.wp_user_id})
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-sm">
                      <div className="mb-1 text-muted-foreground">Ziel-Unterrichtsform{originBadge('unterrichtsform')}</div>
                      <select
                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                        value={stepData.courses_history_check?.target_unterrichtsform_id ?? ''}
                        onChange={(e) => {
                          setStepData((prev) => ({
                            ...prev,
                            courses_history_check: {
                              ...(prev.courses_history_check ?? {}),
                              target_unterrichtsform_id: e.target.value ? Number(e.target.value) : undefined,
                            },
                          }));
                          setMappingOrigins((prev) => ({ ...prev, unterrichtsform: 'manual' }));
                        }}
                      >
                        <option value="">Bitte wählen</option>
                        {targetOptions?.unterrichtsformen?.map((uform) => (
                          <option key={uform.id} value={uform.id}>
                            {uform.name} ({uform.dauer ?? '-'} Min.)
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-sm">
                      <div className="mb-1 text-muted-foreground">Ziel-Wochentag{originBadge('weekday')}</div>
                      <select
                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                        value={stepData.courses_history_check?.target_weekday ?? ''}
                        onChange={(e) => {
                          setStepData((prev) => ({
                            ...prev,
                            courses_history_check: {
                              ...(prev.courses_history_check ?? {}),
                              target_weekday: e.target.value ? Number(e.target.value) : undefined,
                            },
                          }));
                          setMappingOrigins((prev) => ({ ...prev, weekday: 'manual' }));
                        }}
                      >
                        <option value="">Bitte wählen</option>
                        {[1, 2, 3, 4, 5, 6, 7].map((weekday) => (
                          <option key={weekday} value={weekday}>
                            {weekdayLabel(weekday)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-sm">
                      <div className="mb-1 text-muted-foreground">Ziel-Uhrzeit{originBadge('start_time')}</div>
                      <input
                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                        type="time"
                        value={stepData.courses_history_check?.target_start_time ?? ''}
                        onChange={(e) => {
                          setStepData((prev) => ({
                            ...prev,
                            courses_history_check: {
                              ...(prev.courses_history_check ?? {}),
                              target_start_time: e.target.value,
                            },
                          }));
                          setMappingOrigins((prev) => ({ ...prev, start_time: 'manual' }));
                        }}
                      />
                    </label>
                    <label className="text-sm md:col-span-2">
                      <div className="mb-1 text-muted-foreground">Zielkurs</div>
                      <select
                        className="w-full rounded-md border border-input bg-background px-3 py-2"
                        value={stepData.courses_history_check?.target_course_id ?? ''}
                        onChange={(e) =>
                          {
                            const selectedCourseId = e.target.value ? Number(e.target.value) : undefined;
                            setStepData((prev) => ({
                              ...prev,
                              courses_history_check: {
                                ...(prev.courses_history_check ?? {}),
                                target_course_id: selectedCourseId,
                              },
                            }));
                            applyCourseAutofill(selectedCourseId, false);
                          }
                        }
                      >
                        <option value="">Bitte wählen</option>
                        {targetOptions?.courses?.map((course) => (
                          <option key={course.id} value={course.id}>
                            {course.name} | {weekdayLabel(course.weekday ?? null)} {course.time_start ?? ''} | {course.unterrichtsform_name ?? '-'}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    Fehlende Legacy-Werte müssen hier manuell zugeordnet werden. Optional kannst du die Felder auch überschreiben, wenn Legacy-Werte vorhanden sind.
                  </div>
                </WizardSection>
                <WizardSection title="Kurse & Historie festlegen">
                  <div className="space-y-2 text-sm">
                    <label className="flex items-center gap-2">
                      <Checkbox
                        checked={!!stepData.courses_history_check?.migrate_courses}
                        onCheckedChange={(checked) =>
                          setStepData((prev) => ({
                            ...prev,
                            courses_history_check: {
                              ...(prev.courses_history_check ?? {}),
                              migrate_courses: Boolean(checked),
                            },
                          }))
                        }
                      />
                      Zugehörige Kurse migrieren
                    </label>
                    <label className="flex items-center gap-2">
                      <Checkbox
                        checked={!!stepData.courses_history_check?.create_missing_courses}
                        onCheckedChange={(checked) =>
                          setStepData((prev) => ({
                            ...prev,
                            courses_history_check: {
                              ...(prev.courses_history_check ?? {}),
                              create_missing_courses: Boolean(checked),
                            },
                          }))
                        }
                      />
                      Fehlende Kurse bei Bedarf neu anlegen
                    </label>
                    <label className="flex items-center gap-2">
                      <Checkbox
                        checked={!!stepData.courses_history_check?.import_history}
                        onCheckedChange={(checked) =>
                          setStepData((prev) => ({
                            ...prev,
                            courses_history_check: {
                              ...(prev.courses_history_check ?? {}),
                              import_history: Boolean(checked),
                            },
                          }))
                        }
                      />
                      Historie importieren
                    </label>
                    <label className="mt-2 flex items-center gap-2">
                      <Checkbox
                        checked={!!stepData.courses_history_check?.confirmed}
                        onCheckedChange={(checked) =>
                          setStepData((prev) => ({
                            ...prev,
                            courses_history_check: {
                              ...(prev.courses_history_check ?? {}),
                              confirmed: Boolean(checked),
                            },
                          }))
                        }
                      />
                      Kurse/Historie wurden geprüft und freigegeben
                    </label>
                  </div>
                  <Textarea
                    className="mt-3"
                    placeholder="Hinweise zu Kursen/Historie (optional)"
                    value={stepData.courses_history_check?.notes ?? ''}
                    onChange={(e) =>
                      setStepData((prev) => ({
                        ...prev,
                        courses_history_check: {
                          ...(prev.courses_history_check ?? {}),
                          notes: e.target.value,
                        },
                      }))
                    }
                  />
                </WizardSection>
              </WizardCard>
            )}

            {(step === 4 || step === 5) && (
              <WizardCard>
                <WizardSection title="Server-Vorschau / Blocking">
                  {checks?.blocking_reasons?.length ? (
                    <ul className="list-disc space-y-1 pl-5 text-sm text-red-300">
                      {checks.blocking_reasons.map((reason, idx) => (
                        <li key={`${reason}-${idx}`}>{reason}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-emerald-300">Keine Blocking-Checks gefunden.</p>
                  )}
                </WizardSection>
                <WizardSection title="Office-Notiz (gesamt)">
                  <Textarea
                    placeholder="Optionale Notiz für den gesamten Ablauf"
                    value={globalNotes}
                    onChange={(e) => setGlobalNotes(e.target.value)}
                  />
                </WizardSection>
                <WizardSection title="Event-Historie">
                  <div className="max-h-44 space-y-2 overflow-y-auto">
                    {details.events.slice(0, 12).map((event) => (
                      <div key={event.id} className="rounded border border-border/70 p-2 text-xs">
                        <div className="font-medium">{event.event_type}</div>
                        <div className="text-muted-foreground">{event.created_at}</div>
                      </div>
                    ))}
                    {details.events.length === 0 && <div className="text-xs text-muted-foreground">Keine Events vorhanden.</div>}
                  </div>
                </WizardSection>
              </WizardCard>
            )}
          </div>
        )}
      </div>
    </WizardShell>
  );
};

export default PersonMigrationWizardModal;
